import { Dispatch, SetStateAction, useState } from "react"

// ** Third Party Components
import { z } from "zod"
import clsx from "clsx"
import axios from "axios"
import { X } from "lucide-react"
import { parseCookies } from "nookies"
import { useQuery } from "@tanstack/react-query"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm, useFieldArray } from "react-hook-form"

// **Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

// ** Types
import { DictType } from "@pma/types"

// ** Variables
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { ALL_ORG_USERS_ENDPOINT, FILES_SHARE, SERVER_URL } from "@pma/utils/api-routes"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Theme Components
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "../common/form"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../common/select"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../common/dialog"
interface ShareFilesFoldersViaEmailProps {
  modalOpen: boolean
  setModalOpen: Dispatch<SetStateAction<boolean>>
  selectedFiles: DictType[] // Assuming DictType is the type for files/folders
  project?: any // Replace 'any' with the correct type if known, e.g. ProjectType
}

const recipientSchema = z.object({
  email: z.string().email("Please enter a valid email address.").optional(),
  name: z.string().optional(),
})

const formSchema = z.object({
  recipients: z.array(recipientSchema).optional(),
  user: z.string().optional(),
})
type FormValues = z.infer<typeof formSchema>

const ShareFilesFoldersViaEmail = ({
  modalOpen,
  setModalOpen,
  selectedFiles,
  project,
}: ShareFilesFoldersViaEmailProps) => {
  const { authConfig } = useAuth()
  const { translate } = useTranslator()
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const orgId = authConfig?.last_organization_id

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      recipients: [],
      user: "",
    },
  })

  const {
    control,
    handleSubmit,
    getValues,
    resetField,
    setError,
    clearErrors,
    formState: { errors, isLoading, isSubmitting },
  } = form

  const {
    fields: recipientFields,
    append: appendRecipient,
    remove: removeRecipient,
  } = useFieldArray({
    control,
    name: "recipients",
  })

  const [inputEmail, setInputEmail] = useState("")
  const [openModal, setOpenModal] = useState(modalOpen)
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])

  const getUsers = async (orgId: string) => {
    const response = await axios.get(
      SERVER_URL + ALL_ORG_USERS_ENDPOINT + `?organization_id=${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const { data: orgUsers } = useQuery({
    queryKey: [`org-users`, orgId],
    queryFn: () => getUsers(orgId),
    enabled: !!orgId,
  })

  const handleOnOpenChange = (open: boolean) => {
    setOpenModal(false)
    setModalOpen(false)
  }

  const handleAddEmail = () => {
    const trimmed = inputEmail.trim()
    const result = recipientSchema.safeParse({ email: trimmed })

    if (!trimmed) {
      setError("recipients", {
        type: "manual",
        message: "Email is required.",
      })
      return
    }

    if (!result.success) {
      setError("recipients", {
        type: "manual",
        message: result.error.errors[0].message,
      })
      return
    }

    const existingEmails = getValues("recipients")?.map((r) => r?.email?.toLowerCase())

    if (existingEmails?.includes(trimmed.toLowerCase())) {
      setError("recipients", {
        type: "manual",
        message: "Email already added.",
      })
      return
    }

    appendRecipient({ email: trimmed })
    setInputEmail("")
    clearErrors("recipients")
  }

  const handleRemoveRecipient = (index: number) => {
    removeRecipient(index)
  }

  const handleAddRole = () => {
    const user = getValues("user")
    const existingRoles = orgUsers?.items?.find((item: any) => item.id == user) || []
    const newRecord = selectedUsers.some((item: any) => item?.id === existingRoles?.id)
    if (!newRecord) setSelectedUsers((prev) => [...prev, existingRoles])
    resetField("user")
  }

  const handleRemoveRole = (id: number) => {
    setSelectedUsers((prev) => prev.filter((item: any) => item?.id !== id))
  }

  const onSubmit = async (data: FormValues) => {
    if (inputEmail.trim()) {
      handleAddEmail()
      return // Return early to re-trigger validation
    }

    const modifyEmail = selectedUsers.map((user: any) => ({ email: user.email }))

    const fileIds: any[] = []
    const folderIds: any[] = []

    selectedFiles.forEach((file) => {
      if (file.as === "file") fileIds.push(file.id)
      else folderIds.push(file.id)
    })
    const emailArray: any = []
    if (data && data.recipients) {
      data.recipients.forEach((element) => {
        emailArray.push(element)
      })
    }
    if (modifyEmail && modifyEmail.length > 0) {
      modifyEmail.forEach((element) => {
        emailArray.push(element)
      })
    }

    const maxLength = Math.max(emailArray.length, modifyEmail.length)

    const mergedArray = Array.from({ length: maxLength }).map((_, index) => ({
      email: emailArray[index]?.email || "",
      name: "",
    }))

    const requestPayload = {
      files_id: fileIds,
      folders_id: folderIds,
      project_id: project?.id,
      recipients: mergedArray,
    }

    try {
      const response = await axios.post(SERVER_URL + FILES_SHARE, requestPayload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      })
      if (response.status === 200) {
        setModalOpen(false)
        setOpenModal(false)
        form.reset()
        setInputEmail("")
        setSelectedUsers([])
        removeRecipient(0) // Clear recipient fields
        toast({
          title: translate("Success"),
          description: translate("Email Send Successfully"),
          variant: "success",
        })
      }
    } catch (error: any) {
      toast({
        title: translate("Error"),
        description: translate(error?.response?.data?.message || "An Error Occurred"),
        variant: "error",
      })
    }
  }

  return (
    <Dialog open={openModal} onOpenChange={handleOnOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{translate("Share Emails or Users")}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
            {/* Email Input Field */}
            <FormItem>
              <FormLabel>{translate("Email")}</FormLabel>
              <FormControl>
                <div
                  className={clsx(
                    "flex min-h-[42px] w-full flex-wrap items-center gap-2 rounded border px-2 py-1 focus-within:ring-2 focus-within:ring-ring",
                    errors.recipients ? "border-red-500" : "border-gray-300",
                  )}
                >
                  {recipientFields.map((field, index) => (
                    <span
                      key={field.id}
                      className='flex items-center rounded-full bg-gray-200 px-2 py-1 text-sm'
                    >
                      {field.email}
                      <X
                        size={14}
                        className='ml-2 cursor-pointer'
                        onClick={() => handleRemoveRecipient(index)}
                      />
                    </span>
                  ))}

                  <input
                    type='email'
                    value={inputEmail}
                    onChange={(e) => setInputEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                        handleAddEmail()
                      }
                    }}
                    placeholder={translate("Enter email and press Enter")}
                    className='min-w-[120px] flex-1 border-none bg-transparent py-1 text-sm outline-none'
                  />
                </div>
              </FormControl>
              <FormMessage>{errors.recipients?.message}</FormMessage>
            </FormItem>

            {/* User Select with Add Button */}
            <FormField
              control={control}
              name='user'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{translate("User")}</FormLabel>
                  <FormControl>
                    <div className='flex gap-2'>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value)
                          handleAddRole()
                        }}
                        value={field.value}
                      >
                        <SelectTrigger className='w-full'>
                          <SelectValue placeholder={translate("Select a user")} />
                        </SelectTrigger>
                        <SelectContent>
                          {orgUsers?.items?.map((user: any, index: number) => (
                            <SelectItem value={`${user?.id}`} className='rounded-lg' key={index}>
                              {user?.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {/* <button
                        type='button'
                        onClick={handleAddRole}
                        className='rounded bg-blue-600 px-3 text-white'
                      >
                        <Plus size={16} />
                      </button> */}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Selected Roles Chip View */}
            {selectedUsers.length > 0 && (
              <div className='mt-2 flex flex-wrap gap-2'>
                {selectedUsers.map((user: any, index: number) => (
                  <span
                    key={index}
                    className='flex items-center rounded-full bg-gray-200 px-2 py-1 text-sm'
                  >
                    {user?.name}
                    <X
                      size={14}
                      className='ml-2 cursor-pointer'
                      onClick={() => handleRemoveRole(user.id)}
                    />
                  </span>
                ))}
              </div>
            )}

            <DialogFooter>
              <button
                type='submit'
                disabled={isLoading || isSubmitting}
                className='w-full rounded bg-blue-600 py-2 text-white'
              >
                {isLoading || isSubmitting ? translate("Loading") + "..." : translate("Submit")}
              </button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default ShareFilesFoldersViaEmail
